import { BigNumber } from '@ethersproject/bignumber'
import { useMemo } from 'react'
import { useMaintenanceMargin } from './useMaintenanceMargin'
import { useRealTimeUnrealizedPnL } from './useRealTimeUnrealizedPnL'
import { useTotalRequiredMargin } from './useTotalRequiredMargin'

/**
 * Trading Requirement Margin = Total Position Required Margin - Unrealized[- Unrealized Profits + Unrealized Losses].
 * If the margin requirement is less than the sum of the minimum maintenance margins for each position,
 * then return the sum of the minimum maintenance margins.
 *
 * @returns The trading requirement margin as a BigNumber.
 */
export function usePerpetualRequirementMargin(): BigNumber {
  const requiredMargin = useTotalRequiredMargin()
  const totalUnrealizedPNL = useRealTimeUnrealizedPnL()
  const maintenanceMargin = useMaintenanceMargin()

  return useMemo(() => {
    const finalRequiredMargin = requiredMargin.sub(totalUnrealizedPNL)

    return finalRequiredMargin.lt(maintenanceMargin)
      ? maintenanceMargin
      : finalRequiredMargin
  }, [requiredMargin, totalUnrealizedPNL, maintenanceMargin])
}

/**
 * Calculates the transfer/withdrawal/spot-trading requirement margin based on the total required margin and the real-time unrealized PnL.
 * If the total unrealized PnL is greater than 0, the required margin is returned.
 * Otherwise, the required margin is subtracted by the total unrealized PnL.
 *
 * @returns The transfer requirement margin as a BigNumber.
 */
export function useTransferRequirementMargin(): BigNumber {
  const requiredMargin = useTotalRequiredMargin()
  const totalUnrealizedPNL = useRealTimeUnrealizedPnL()

  return useMemo(() => {
    if (totalUnrealizedPNL.gt(0)) {
      return requiredMargin
    } else {
      return requiredMargin.sub(totalUnrealizedPNL)
    }
  }, [requiredMargin, totalUnrealizedPNL])
}

/**
 * Calculates the requirement margin for liquidation.
 *
 * @returns The requirement margin as a BigNumber.
 */
export function useLiquidationRequirementMargin(): BigNumber {
  const totalUnrealizedPNL = useRealTimeUnrealizedPnL()
  const maintenanceMargin = useMaintenanceMargin()

  return useMemo(() => {
    return maintenanceMargin.sub(totalUnrealizedPNL)
  }, [totalUnrealizedPNL, maintenanceMargin])
}
